import { Routes, Route, Navigate, Outlet } from "react-router-dom";
import { usePageTracking } from "../analytics/usePageTracking";
import { AppShell } from "../components/layout/AppShell";
import {
  RequireAuth,
  LoginRoute,
  InviteLoginRedirect,
  SignupRoute,
  CheckEmailRoute,
  AuthActionRoute,
  ForgotPasswordRoute,
  ComingSoonScreen,
  CliLoginRoute,
  LOGIN_PATH,
  INVITE_LOGIN_PATH,
  AUTH_ACTION_PATH,
  SIGNUP_PATH,
  CHECK_EMAIL_PATH,
  FORGOT_PASSWORD_PATH,
  COMING_SOON_PATH,
  CLI_LOGIN_PATH,
} from "../auth";
import { IdentityProvider } from "../identity";
import { FlagsProvider } from "../services/flags";
import { StiggProvider } from "../services/stigg";
import { WorkspaceRedirect, WorkspaceShell } from "../workspaces";
import { MobileGuard } from "./MobileGuard";
import WorkspaceHome from "../pages/WorkspaceHome";
import AgentDetailPage from "../pages/AgentDetailPage";
import WelcomePage from "../pages/welcome/WelcomePage";
import PublicWelcomePage from "../pages/welcome/PublicWelcomePage";
import AcceptInvitationPage from "../pages/AcceptInvitationPage";
import MembersPage from "../pages/org/MembersPage";
import ApiKeysPage from "../pages/settings/ApiKeysPage";
import BillingPage from "../pages/settings/BillingPage";
import OrganizationPage from "../pages/org/OrganizationPage";

/* Gates the app behind auth and mounts the identity service (workspaces,
 * org members, API keys). The gateway data providers live INSIDE WorkspaceShell
 * — they are workspace-scoped and remount per workspace. The login route is a
 * public sibling so it renders without any of this. */
function GatedApp() {
  return (
    <RequireAuth>
      {/* Mobile wall (OPSS-246). Inside the auth gate so signing in still works
        on a phone, and outside the providers below so nothing is fetched for a
        visitor who only sees the desktop-only screen. */}
      <MobileGuard>
        <IdentityProvider>
          {/* Flags sit inside identity (they fetch for the signed-in user) and
            outside Stigg (useTokenwisePlan reads the stigg-enabled flag). */}
          <FlagsProvider>
            <StiggProvider>
              <Outlet />
            </StiggProvider>
          </FlagsProvider>
        </IdentityProvider>
      </MobileGuard>
    </RequireAuth>
  );
}

function Router() {
  // One place to fire PageEntered for every route — gated pages and the
  // public/gated workspace-less pages (auth, docs link, invite) alike.
  usePageTracking();

  return (
    <Routes>
      {/* Public: the auth screens live outside the gate + data providers. */}
      <Route path={LOGIN_PATH} element={<LoginRoute />} />
      {/* /auth is the URL studio's invite emails point at; redirect it to the
        canonical /login, query intact. */}
      <Route path={INVITE_LOGIN_PATH} element={<InviteLoginRedirect />} />
      {/* Firebase email-action links (the project's template action URL):
        verification is applied in-app, other modes forward to the Firebase-
        hosted handler. */}
      <Route path={AUTH_ACTION_PATH} element={<AuthActionRoute />} />
      <Route path={SIGNUP_PATH} element={<SignupRoute />} />
      {/* Verified gate for unverified email/password users + the forgot-password
        flow. Public siblings — they manage their own auth-status redirects. */}
      <Route path={CHECK_EMAIL_PATH} element={<CheckEmailRoute />} />
      <Route path={FORGOT_PASSWORD_PATH} element={<ForgotPasswordRoute />} />
      {/* Public page for allowlist-rejected sign-ins. */}
      <Route path={COMING_SOON_PATH} element={<ComingSoonScreen />} />
      {/* Consent screen for a `hopt` CLI sign-in. Public on purpose: inside GatedApp it
        would inherit MobileGuard (this flow starts at a desktop terminal) and the
        identity/flags/Stigg fetch stack, none of which it needs — it needs the Firebase
        session and nothing else. It redirects to /login itself when not signed in. */}
      <Route path={CLI_LOGIN_PATH} element={<CliLoginRoute />} />

      <Route element={<GatedApp />}>
        {/* Workspace-less entry: resolve the default workspace. */}
        <Route path="/" element={<WorkspaceRedirect />} />

        {/* Workspace-less entry to the first-run wizard. Resolves the default
          workspace and redirects to /w/:id/welcome (the real full-screen page,
          which needs workspace-scoped data). Clean shareable /welcome URL. */}
        <Route path="/welcome" element={<PublicWelcomePage />} />

        {/* Accept an org invitation. Gated (the viewer must be signed in) but
          workspace-less, since the invitee may not belong to any workspace in
          the inviting org yet. Invite emails link to /auth?invitation_id=…
          (which redirects to /login); once authenticated, LoginRoute forwards
          here. */}
        <Route
          path="/invite/:invitationId"
          element={<AcceptInvitationPage />}
        />

        {/* Everything lives under the active workspace. */}
        <Route path="/w/:workspaceId" element={<WorkspaceShell />}>
          {/* First-run wizard: full-screen (no app chrome), but inside
            WorkspaceShell so it has the workspace data providers. Reachable via
            the empty-agents state and the shareable /welcome link. */}
          <Route path="welcome" element={<WelcomePage />} />

          {/* Every gated page shares the product-design shell (AppShell: dark
            maroon top bar + light body, no sidebar) — the home Overview and the
            settings/org pages alike. Navigation to settings/org lives in the
            header profile menu. AppShell sits inside WorkspaceShell so it has
            the workspace data providers (analytics, filters, agents). */}
          <Route element={<AppShell />}>
            {/* Home: post-onboarding landing — the Overview dashboard. Redirects
              to the getting-started wizard until the workspace has an agent. */}
            <Route index element={<WorkspaceHome />} />

            {/* Per-agent detail — drill-in from the Overview's Connected agents
              list. Temporary "in progress" placeholder until the full view is
              built. */}
            <Route path="agents/:agentId" element={<AgentDetailPage />} />

            {/* Settings pages (linked from the user menu, not the main nav):
            workspace-level under /settings, organization-level under /org
            (org-scoped data; the workspace prefix is kept only so the
            chrome stays consistent). */}
            <Route path="settings">
              <Route index element={<Navigate to="api-keys" replace />} />
              <Route path="api-keys" element={<ApiKeysPage />} />
              <Route path="billing" element={<BillingPage />} />
            </Route>
            <Route path="org">
              <Route index element={<OrganizationPage />} />
              <Route path="members" element={<MembersPage />} />
            </Route>
          </Route>
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default Router;
